/**
 * Diff Highlighting Module
 *
 * Applies visual highlighting to show differences between a snapshot and current state.
 * Uses conditional formatting (instead of direct fills) to preserve gridlines.
 * Manages state to restore original formatting when highlights are cleared.
 *
 * Performance optimization: Groups adjacent cells into rectangles to minimize
 * the number of conditional format rules (e.g., 10,000 cells → 50-200 rectangles).
 */

import {
  columnLetterToNumber,
  columnNumberToLetter,
  parseCellAddress,
} from '../agents/tool-utils'
import { getExcelRuntime } from '../excel-utils'
import type { CellDiff, Diff } from './diff'

// ============================================================================
// Types
// ============================================================================

interface HighlightState {
  originalTabColors: Map<string, string | null>
  // Map of "sheetId!rangeAddress" -> conditional format ID (ranges can be rectangles)
  conditionalFormatIds: Map<string, string>
  isActive: boolean
}

interface Rectangle {
  startCol: number
  endCol: number
  startRow: number
  endRow: number
}

// ============================================================================
// Rectangle Grouping Algorithm
// ============================================================================

/**
 * Build optimized address groups by merging adjacent cells into rectangles.
 * This dramatically reduces the number of conditional format rules needed.
 *
 * Algorithm:
 * 1. Parse addresses to coordinates, sort by row then column
 * 2. Find horizontal segments (contiguous cells in same row)
 * 3. Merge segments vertically when they share the same column span
 *
 * Example: 100 individual cells in a 10x10 block → 1 rectangle "A1:J10"
 */
function buildAddressGroups(addresses: string[]): string[] {
  if (addresses.length === 0) return []
  if (addresses.length === 1) return addresses

  // Parse all addresses to coordinates
  const coords = addresses.map((addr) => {
    const { col, row } = parseCellAddress(addr)
    return { col: columnLetterToNumber(col), row }
  })

  // Sort by row, then by column
  coords.sort((a, b) => a.row - b.row || a.col - b.col)

  // Step 1: Build horizontal segments for each row
  // A segment is a contiguous run of columns in the same row
  const segments: Array<{ row: number; startCol: number; endCol: number }> = []
  let currentSegment: { row: number; startCol: number; endCol: number } | null =
    null

  for (const coord of coords) {
    if (
      currentSegment &&
      currentSegment.row === coord.row &&
      coord.col === currentSegment.endCol + 1
    ) {
      // Extend current segment
      currentSegment.endCol = coord.col
    } else {
      // Start new segment
      if (currentSegment) {
        segments.push(currentSegment)
      }
      currentSegment = {
        row: coord.row,
        startCol: coord.col,
        endCol: coord.col,
      }
    }
  }
  if (currentSegment) {
    segments.push(currentSegment)
  }

  // Step 2: Merge segments vertically into rectangles
  // Track "open" rectangles that can extend downward
  const rectangles: Rectangle[] = []
  const openRects: Rectangle[] = []

  for (const segment of segments) {
    // Try to extend an open rectangle
    let merged = false
    for (let i = 0; i < openRects.length; i++) {
      const rect = openRects[i]
      if (
        rect.startCol === segment.startCol &&
        rect.endCol === segment.endCol &&
        rect.endRow + 1 === segment.row
      ) {
        // Extend rectangle downward
        rect.endRow = segment.row
        merged = true
        break
      }
    }

    if (!merged) {
      // Close any open rectangles that can't continue
      // (they're from earlier rows or different column spans)
      for (let i = openRects.length - 1; i >= 0; i--) {
        const rect = openRects[i]
        if (rect.endRow < segment.row - 1) {
          rectangles.push(rect)
          openRects.splice(i, 1)
        }
      }
      // Start a new rectangle
      openRects.push({
        startCol: segment.startCol,
        endCol: segment.endCol,
        startRow: segment.row,
        endRow: segment.row,
      })
    }
  }

  // Close remaining open rectangles
  rectangles.push(...openRects)

  // Convert rectangles to Excel address strings
  return rectangles.map((rect) => {
    const startAddr = `${columnNumberToLetter(rect.startCol)}${rect.startRow}`
    if (rect.startCol === rect.endCol && rect.startRow === rect.endRow) {
      return startAddr
    }
    const endAddr = `${columnNumberToLetter(rect.endCol)}${rect.endRow}`
    return `${startAddr}:${endAddr}`
  })
}

// ============================================================================
// Color Constants
// ============================================================================

const COLORS = {
  added: '#C6EFCE', // Light green
  deleted: '#FFC7CE', // Light red
  'formula-result-changed': '#FFFF99', // Light yellow
  'value-changed': '#FFEB9C', // Light orange
  sheetTab: '#FF9900', // Orange (for sheets with changes)
  sheetTabAdded: '#00B050', // Green (for added sheets)
} as const

type DiffColorType =
  | 'added'
  | 'deleted'
  | 'formula-result-changed'
  | 'value-changed'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the highlight color for a diff type
 */
function getColorForDiffType(type: DiffColorType): string {
  switch (type) {
    case 'added':
      return COLORS.added
    case 'deleted':
      return COLORS.deleted
    case 'formula-result-changed':
      return COLORS['formula-result-changed']
    case 'value-changed':
      return COLORS['value-changed']
  }
}

/**
 * Group cells by their diff type for batch processing
 */
function groupCellsByType(
  cells: CellDiff[]
): Map<CellDiff['type'], CellDiff[]> {
  const groups = new Map<CellDiff['type'], CellDiff[]>()
  for (const cell of cells) {
    const existing = groups.get(cell.type)
    if (existing) {
      existing.push(cell)
    } else {
      groups.set(cell.type, [cell])
    }
  }
  return groups
}

// ============================================================================
// DiffHighlighter Class
// ============================================================================

class DiffHighlighter {
  private state: HighlightState = {
    originalTabColors: new Map(),
    conditionalFormatIds: new Map(),
    isActive: false,
  }

  /**
   * Apply highlights to show differences
   * Uses rectangle grouping to minimize the number of conditional format rules.
   */
  async applyHighlights(diff: Diff): Promise<void> {
    // Clear any existing highlights first
    if (this.state.isActive) {
      await this.clearHighlights()
    }

    const Excel = getExcelRuntime()

    await Excel.run(async (context: Excel.RequestContext) => {
      const worksheets = context.workbook.worksheets
      worksheets.load('items')
      await context.sync()

      // Load properties for all sheets
      for (const sheet of worksheets.items) {
        sheet.load(['id', 'name', 'tabColor'])
      }
      await context.sync()

      // First, highlight added sheets (sheets that didn't exist in snapshot)
      const addedSheetIds = new Set(diff.addedSheets.map((s) => s.id))
      for (const addedSheetInfo of diff.addedSheets) {
        const sheet = worksheets.items.find((s) => s.id === addedSheetInfo.id)
        if (sheet) {
          const currentTabColor = sheet.tabColor
          this.state.originalTabColors.set(addedSheetInfo.id, currentTabColor)
          sheet.tabColor = COLORS.sheetTabAdded // Green for added sheets
        }
      }

      // Collect all conditional formats to apply, then batch sync
      const formatsToTrack: Array<{
        key: string
        format: Excel.ConditionalFormat
      }> = []

      for (const sheetDiff of diff.sheets) {
        const sheet = worksheets.items.find((s) => s.id === sheetDiff.sheetId)
        if (!sheet) {
          console.warn(
            `Sheet ${sheetDiff.sheetName} (${sheetDiff.sheetId}) not found, skipping`
          )
          continue
        }

        // Store and set tab color for sheets with changes (but not added sheets - they're already green)
        if (sheetDiff.hasChanges && !addedSheetIds.has(sheetDiff.sheetId)) {
          const currentTabColor = sheet.tabColor
          this.state.originalTabColors.set(sheetDiff.sheetId, currentTabColor)
          sheet.tabColor = COLORS.sheetTab // Orange for modified sheets
        }

        // Group cells by diff type, then build rectangles for each type
        const cellsByType = groupCellsByType(sheetDiff.cells)

        for (const [diffType, cells] of Array.from(cellsByType)) {
          if (diffType === 'unchanged') continue

          const addresses = cells.map((c) => c.address)
          const rectangles = buildAddressGroups(addresses)

          const color = getColorForDiffType(diffType)

          // Apply one conditional format per rectangle (instead of per cell)
          for (const rangeAddress of rectangles) {
            const range = sheet.getRange(rangeAddress)
            const formatKey = `${sheetDiff.sheetId}!${rangeAddress}`

            const conditionalFormat = range.conditionalFormats.add(
              Excel.ConditionalFormatType.cellValue
            )
            const cellValueRule = conditionalFormat.cellValue
            cellValueRule.format.fill.color = color
            cellValueRule.rule = {
              formula1: '-999999',
              operator: Excel.ConditionalCellValueOperator.greaterThanOrEqual,
            }

            conditionalFormat.load('id')
            formatsToTrack.push({ key: formatKey, format: conditionalFormat })
          }
        }
      }

      // Single sync to apply all formats and get their IDs
      await context.sync()

      // Store all format IDs
      for (const { key, format } of formatsToTrack) {
        this.state.conditionalFormatIds.set(key, format.id)
      }

      console.log(
        `Applied ${formatsToTrack.length} conditional format rectangles`
      )
    })

    this.state.isActive = true
    console.log('Applied diff highlights')
  }

  /**
   * Clear all highlights and restore original formatting
   * Deletes conditional formats (preserves gridlines)
   *
   * Uses a robust approach: clears ALL sheets with our highlight colors,
   * not just sheets we have stored IDs for. This handles edge cases like
   * state loss or sheet ID changes.
   */
  async clearHighlights(): Promise<void> {
    if (!this.state.isActive) return

    const Excel = getExcelRuntime()

    await Excel.run(async (context: Excel.RequestContext) => {
      const worksheets = context.workbook.worksheets
      worksheets.load('items')
      await context.sync()

      // Load sheet properties including tabColor
      for (const sheet of worksheets.items) {
        sheet.load(['id', 'name', 'tabColor'])
      }
      await context.sync()

      // Group format IDs by sheet for efficient deletion
      const formatsBySheet = new Map<
        string,
        Array<{ address: string; formatId: string }>
      >()

      for (const [key, formatId] of Array.from(
        this.state.conditionalFormatIds
      )) {
        const [sheetId, address] = key.split('!')
        if (!formatsBySheet.has(sheetId)) {
          formatsBySheet.set(sheetId, [])
        }
        const sheetFormats = formatsBySheet.get(sheetId)
        if (sheetFormats) {
          sheetFormats.push({ address, formatId })
        }
      }

      // Delete conditional formats for each sheet
      for (const [sheetId, formats] of Array.from(formatsBySheet)) {
        const sheet = worksheets.items.find((s) => s.id === sheetId)
        if (!sheet) continue

        try {
          // Load all conditional formats for each range, batch the loads
          const rangeFormats: Array<{
            formatId: string
            conditionalFormats: Excel.ConditionalFormatCollection
          }> = []

          for (const { address, formatId } of formats) {
            try {
              const range = sheet.getRange(address)
              const conditionalFormats = range.conditionalFormats
              conditionalFormats.load('items')
              rangeFormats.push({ formatId, conditionalFormats })
            } catch {
              // Range might not exist anymore, skip
            }
          }

          await context.sync()

          // Load IDs for all format items
          for (const { conditionalFormats } of rangeFormats) {
            for (const cf of conditionalFormats.items) {
              cf.load('id')
            }
          }
          await context.sync()

          // Delete matching formats
          for (const { formatId, conditionalFormats } of rangeFormats) {
            for (const cf of conditionalFormats.items) {
              if (cf.id === formatId) {
                cf.delete()
              }
            }
          }

          // Ensure gridlines are visible (safeguard)
          sheet.showGridlines = true
        } catch (error) {
          console.warn(
            `Failed to clear conditional formats for sheet ${sheetId}`,
            error
          )
        }
      }

      // Robust tab color restoration: clear ANY sheet with our highlight colors
      // This handles cases where stored IDs don't match current sheet IDs
      const highlightColors = new Set([
        COLORS.sheetTab.toUpperCase(), // Orange for modified
        COLORS.sheetTabAdded.toUpperCase(), // Green for added
      ])

      for (const sheet of worksheets.items) {
        const tabColor = sheet.tabColor?.toUpperCase() || ''
        if (highlightColors.has(tabColor)) {
          // This sheet has one of our highlight colors - restore it
          const originalColor = this.state.originalTabColors.get(sheet.id)
          sheet.tabColor = originalColor || ''
        }
      }

      await context.sync()
    })

    // Reset state
    this.state = {
      originalTabColors: new Map(),
      conditionalFormatIds: new Map(),
      isActive: false,
    }

    console.log('Cleared diff highlights')
  }

  /**
   * Check if highlighting is currently active
   */
  isComparing(): boolean {
    return this.state.isActive
  }
}

// ============================================================================
// Export Singleton Instance
// ============================================================================

export const diffHighlighter = new DiffHighlighter()
